import * as Yup from 'yup';
import { iAddress, iAttachments, iContactMin, iFVSCheckboxAll, iFormMetaData, iObligation, iSummary } from '@interfaces/interfaces';
import { FormType, ProgressStep } from '@constants';
import { ADDRESS_FIELD_CHARS_REGEX, COMPANY_NAME_REGEX, CONTACT_NUMBER_REGEX, CRN_REGEX, EMAIL_SPECIAL_CHARS_REGEX, FREE_TEXT_FIELD_REGEX, NAMES_FIELDS_REGEX, OBLIGATION_REGEX, ONLY_NUMBERS_REGEX, POSITION_IN_COMPANY_REGEX, POST_CODE_REGEX, VEHICLE_REGISTRATION_REGEX } from '@services/ValidationRules';

import { FormikValues } from 'formik';
import { EMPTY_CHECKBOX, EMPTY_DATE, FILE_UPLOAD_FAIL, INVALID_ADDRESS_FIELD_ERROR, INVALID_ADDRESS_SEARCH_FORMAT, INVALID_COMPANY_NAME, INVALID_CRN, INVALID_EMAIL, INVALID_FILE_SIZE_CUSTOM, INVALID_FIRST_INSTALMENT_DATE, INVALID_FREE_TEXT_FIELD_FORMAT, INVALID_FUTURE_DATE, INVALID_INPUT_LONGER_THAN_100_CHARS, INVALID_INPUT_LONGER_THAN_10_CHARS, INVALID_INPUT_LONGER_THAN_200_CHARS, INVALID_INPUT_LONGER_THAN_40_CHARS, INVALID_INPUT_LONGER_THAN_50_CHARS, INVALID_INPUT_LONGER_THAN_75_CHARS, INVALID_INPUT_LONGER_THAN_80_CHARS, INVALID_LICENCE_FORMAT, INVALID_MIN_PAYMENT_FORTNIGHTLY, INVALID_MIN_PAYMENT_HIGH, INVALID_MIN_PAYMENT_LOW, INVALID_MOBILE_NUMBER_FORMAT, INVALID_MONETARY_AMOUNT, INVALID_NAME_FORMAT_NEW, INVALID_NUMBER_INPUT, INVALID_NUMERIC_FORMAT, INVALID_OBLIGATION_NUMBER, INVALID_PHONE_NUMBER, INVALID_POSITION_IN_COMPANY_FIELD, INVALID_POST_CODE, INVALID_REPAYMENT_TIME_LENGTH, INVALID_VEHICLE_REGISTRATION_NUMBER, INVALID_YESNO_FORMAT, MISSING_DECLARATION_AGREEMENT, INVALID_PHONE_NUMBER_LENGTH_15, INVALID_PHONE_NUMBER_LENGTH } from './ErrorMessages';
import keys from './PageValidationKeys';

const { INSTALMENT } = FormType;

const today = new Date();
const dateAfter21Days = new Date();
dateAfter21Days.setDate(new Date().getDate() + 21);

export interface iPartialFormData {
  a: string;
  submitted: boolean;
  // Step 1
  OBLIGATION_NUMBER: string;
  isDebtorTypeIndividual: boolean | null;
  isDebtorTypeCompany: boolean | null;
  // Step 3
  OFFENCE_DATE: Date | null;
  HEARING_DATE: Date | null;
  VEHICLE_REGISTRATION: string;
  DRIVER_LICENCE: string;
  DOB: Date | null;

  // Step 4
  SaveYesNo: string;
  SaveOptions: string;
  saveEmail: string;
  saveMobile: string;

  // Step 5
  SelectedObligationsStep5: iFVSCheckboxAll[];
  TotalPaymentArrangementAmount: string;
  TotalPaymentArrangementAmountDisplay: string;
  AuthorisedObligation: iObligation;
  InfringementNoticeNumber: string;
  IneligibleObligations: iObligation[];
  EligibleObligations: iObligation[];
  ArrangedObligations: { [PANumber: string]: iObligation[] };
  AdditionalObligationsNum: number;

  // Step 6
  MoreInfoAboutMatters: string[];
  NoInfoApplies: boolean;

  // Step 7
  SupportingDocumentsUpfrontPayment: iAttachments[];
  DetailsForUpfrontPaymentCircumstances: string;
  SupportingDocUpfrontPaymentNameField: string;
  SupportingDocUpfrontPaymentFilesDescription: string;
  SupportingDocUpfrontPaymentFileUploadErrorHandle: boolean;
  SupportingDocUpfrontPaymentUploadFileSectionError: string;
  SupportingDocUpfrontPaymentFilesProvider: string;
  SupportingDocUpfrontPaymentFieldErrorHandle: boolean;
  isStep7ContinueApplicationClicked: boolean;

  // Step 8
  receiveCenterlinkPayments: string;
  centrelinkPaymentType: string;
  instalmentDeducted: string;
  specifyPaymentType: string;
  centrelinkRefNo: string;
  fortnightlyPaymentAmount:string;
  // centrelinkCheck: boolean;

    // Step 9
  instalmentFrequency: string;
  instalmentAmount: string;
  instalmentNumberOfPayments: string;
  firstInstalmentDate: Date | null;

  // Step 10
  haveExceptionalCircumstances:string;
  SupportingFinancialDocuments: iAttachments[];
  DetailsOfFinancialCircumstances: string;
  SupportingFinancialDocNameField: string;
  SupportingFinancialDocFilesDescription: string;
  SupportingFinancialDocFileUploadErrorHandle: boolean;
  SupportingFinancialDocUploadFileSectionError: string;
  SupportingFinancialDocFilesProvider: string;
  SupportingFinancialDocFieldErrorHandle: boolean;
  numberOfDependents: string;
  FileSizeLimitFinancialDocs: string;

  // Step 11
  fortnightlyIncomeAfterTax: string;
  fortnightlyExpenses: string;
  quarterlyProfit: string;
  quarterlyLoss: string;

  // Step 12
  IndividualFamilyName: string;
  IndividualGivenName: string;
  // Step 12 Individual License
  LicenceState: string;
  NoDriverLicence: boolean;
  StatecountryOfIssue: string;
  // Step 12 Residential Address
  IndividualPostalAddress:iAddress;
  IndividualHasDifferentResidentialAddress:string;
  IndividualResidentialAddress:iAddress;
  // Step 12 Individual Contact
  IndividualContactNumber:iContactMin;
  IndividualHasNoEmail:string;
  IndividualEmail:string;
  IndividualContactNumberWarning: string;
  IndividualEmailWarning: string;
  // Step 12 Consent Questions
  // IndividualEmailTo:string;
  IndividualReceiveDocs:string;

  // Step 12 Company
  CompanyGivenName: string;
  CompanyFamilyName: string;
  PositionInCompany: string;
  CompanyContactNumber: iContactMin;
  CompanyHasNoEmail: string;
  CompanyEmail: string;
  CompanyReceiveDocs: string;
  CompanyContactNumberWarning: string;
  CompanyEmailWarning: string;
  // CompanyEmailTo: string;
  CompanyName: string;
  AbnOrAcnType: string;
  AbnNo: string;
  AcnNo: string;
  CompanyStreetAddress: iAddress;
  CompanyHasDifferentPostalAddress: string;
  CompanyPostalAddress: iAddress;

  // Step 14
  declaration: boolean;
}

interface iPartialFormObligations {
  ID: number;
  ObligationNO: string;
  IsPrimary: string;
}
export interface iCommonInstalmentValues {
  [key: string]: string | Date | boolean | null | iFVSCheckboxAll[] | string[] | iPartialFormData | iPartialFormObligations[] | iAttachments[] | iAddress | iContactMin | iObligation | iObligation[] | { [PANumber: string]: iObligation[] } | number;
  OBLIGATION_NUMBER: string;
  isDebtorTypeIndividual: boolean | null;
  isDebtorTypeCompany: boolean | null;
  isNominatable: boolean | null;
  // Step 3
  OFFENCE_DATE: Date | null;
  HEARING_DATE: Date | null;
  VEHICLE_REGISTRATION: string;
  DRIVER_LICENCE: string;
  DOB: Date | null;

  // Step 4
  SaveYesNo: string;
  SaveOptions: string;
  saveEmail: string;
  saveMobile: string;
  IsSubscribedEM: boolean;
  IsSubscribedSMS: boolean;
  EMRemeinderSent: boolean;
  SMSRemeinderSent: boolean;

  // Partial Forms
  ID: string;
  PartialFormID: string;
  FormData: iPartialFormData;
  Obligations: iPartialFormObligations[];
}

export interface iInstalmentFormValues extends iCommonInstalmentValues {
  InfringementNumber: string;
  // Step 5
  SelectedObligationsStep5: iFVSCheckboxAll[];
  TotalPaymentArrangementAmount: string;
  TotalPaymentArrangementAmountDisplay: string;

  AuthorisedObligation: iObligation;
  InfringementNoticeNumber: string;
  IneligibleObligations: iObligation[];
  EligibleObligations: iObligation[];
  ArrangedObligations: { [PANumber: string]: iObligation[] };
  AdditionalObligationsNum: number;

  // Step 6
  MoreInfoAboutMatters: string[];
  NoInfoApplies: boolean;

  // Step 7
  SupportingDocumentsUpfrontPayment: iAttachments[];
  DetailsForUpfrontPaymentCircumstances: string;
  SupportingDocUpfrontPaymentNameField: string;
  SupportingDocUpfrontPaymentFilesDescription: string;
  SupportingDocUpfrontPaymentFileUploadErrorHandle: boolean;
  SupportingDocUpfrontPaymentUploadFileSectionError: string;
  SupportingDocUpfrontPaymentFilesProvider: string;
  SupportingDocUpfrontPaymentFieldErrorHandle: boolean;
  isStep7ContinueApplicationClicked: boolean;

  // Step 8
  receiveCenterlinkPayments: string;
  centrelinkPaymentType: string;
  instalmentDeducted: string;
  specifyPaymentType: string;
  centrelinkRefNo: string;
  fortnightlyPaymentAmount:string;
  // centrelinkCheck: boolean;

  // Step 9
  instalmentFrequency: string;
  instalmentAmount: string;
  instalmentNumberOfPayments: string;
  firstInstalmentDate: Date | null;

  // Step 10
  haveExceptionalCircumstances:string;
  SupportingFinancialDocuments: iAttachments[];
  DetailsOfFinancialCircumstances: string;
  SupportingFinancialDocNameField: string;
  SupportingFinancialDocFilesDescription: string;
  SupportingFinancialDocFileUploadErrorHandle: boolean;
  SupportingFinancialDocUploadFileSectionError: string;
  SupportingFinancialDocFilesProvider: string;
  SupportingFinancialDocFieldErrorHandle: boolean;
  numberOfDependents: string;
  FileSizeLimitFinancialDocs: string;

  // Step 11
  fortnightlyIncomeAfterTax: string;
  fortnightlyExpenses: string;
  quarterlyProfit: string;
  quarterlyLoss: string;

  // Step 12
  IndividualFamilyName: string;
  IndividualGivenName: string;
  // Step 12 Individual License
  LicenceState: string;
  NoDriverLicence: boolean;
  StatecountryOfIssue: string;
  // Step 12 Residential Address
  IndividualPostalAddress:iAddress;
  IndividualHasDifferentResidentialAddress:string;
  IndividualResidentialAddress:iAddress;
  // Step 12 Individual Contact
  IndividualContactNumber:iContactMin;
  IndividualHasNoEmail:string;
  IndividualEmail:string;
  IndividualContactNumberWarning: string;
  IndividualEmailWarning: string;
  // Step 12 Consent Questions
  // IndividualEmailTo:string;
  IndividualReceiveDocs:string;

  // Step 12 Company
  CompanyGivenName: string;
  CompanyFamilyName: string;
  PositionInCompany: string;
  CompanyContactNumber: iContactMin;
  CompanyHasNoEmail: string;
  CompanyEmail: string;
  CompanyReceiveDocs: string;
  CompanyContactNumberWarning: string;
  CompanyEmailWarning: string;
  // CompanyEmailTo: string;
  CompanyName: string;
  AbnOrAcnType: string;
  AbnNo: string;
  AcnNo: string;
  CompanyStreetAddress: iAddress;
  CompanyHasDifferentPostalAddress: string;
  CompanyPostalAddress: iAddress;

  // Step 14
  declaration: boolean;

  // Step 15
  submissionNo: string;
  FormExperience: string;
  // Submission Error
  // submission error message
  submissionError: string;
}

export const defaultForm = (): iInstalmentFormValues => {
  const defaultValues: iInstalmentFormValues = {
    OBLIGATION_NUMBER: '',
    InfringementNumber: '',
    isDebtorTypeCompany: null,
    isDebtorTypeIndividual: null,
    isNominatable: null,
    // Step 3
    OFFENCE_DATE: null,
    HEARING_DATE: null,
    DRIVER_LICENCE: '',
    VEHICLE_REGISTRATION: '',
    DOB: null,

    // Step 4
    SaveYesNo: '',
    SaveOptions: '',
    saveEmail: '',
    saveMobile: '',
    IsSubscribedEM: false,
    IsSubscribedSMS: false,
    EMRemeinderSent: false,
    SMSRemeinderSent: false,
    // Step 5
    SelectedObligationsStep5: [],
    TotalPaymentArrangementAmount: '',
    TotalPaymentArrangementAmountDisplay: '',

    AuthorisedObligation: {} as iObligation,
    InfringementNoticeNumber: '',
    IneligibleObligations: [],
    EligibleObligations: [],
    ArrangedObligations: {} as { [PANumber: string]: iObligation[] },
    AdditionalObligationsNum: 0,
    // Step 6
    MoreInfoAboutMatters: [],
    NoInfoApplies: false,
    // WheelClamped: false,
    // ReceivedDirectorNotice: false,
    // VehicleSeized: false,
    // PlatesRemoved: false,
    // LicenseSactioned: false,

    // Step 7
    SupportingDocumentsUpfrontPayment: [],
    DetailsForUpfrontPaymentCircumstances: '',
    SupportingDocUpfrontPaymentNameField: '',
    SupportingDocUpfrontPaymentFileUploadErrorHandle: false,
    SupportingDocUpfrontPaymentUploadFileSectionError: '',
    SupportingDocUpfrontPaymentFilesProvider: '',
    SupportingDocUpfrontPaymentFilesDescription: '',
    SupportingDocUpfrontPaymentFieldErrorHandle: false,
    isStep7ContinueApplicationClicked: false,

    // Step 8
    receiveCenterlinkPayments: '',
    centrelinkPaymentType: '',
    instalmentDeducted: '',
    specifyPaymentType: '',
    centrelinkRefNo: '',
    fortnightlyPaymentAmount: '',
    // centrelinkCheck: false,

    // Step 9
    instalmentFrequency: '',
    instalmentAmount: '',
    instalmentNumberOfPayments: '',
    firstInstalmentDate: dateAfter21Days,

    // Step 10
    haveExceptionalCircumstances: '',
    SupportingFinancialDocuments: [],
    DetailsOfFinancialCircumstances: '',
    SupportingFinancialDocNameField: '',
    SupportingFinancialDocFileUploadErrorHandle: false,
    SupportingFinancialDocUploadFileSectionError: '',
    SupportingFinancialDocFilesProvider: '',
    SupportingFinancialDocFilesDescription: '',
    SupportingFinancialDocFieldErrorHandle: false,
    numberOfDependents: '',
    FileSizeLimitFinancialDocs: '4',

    // Step 11
    fortnightlyIncomeAfterTax: '',
    fortnightlyExpenses: '',
    quarterlyProfit: '',
    quarterlyLoss: '',

    // Step 12
    IndividualFamilyName: '',
    IndividualGivenName: '',
    // Step 12 Individual License
    LicenceState: '',
    NoDriverLicence: false,
    StatecountryOfIssue: 'Australia',
    // Step 12 Residential Address
    IndividualPostalAddress: {
      SearchText: '',
      Address: '',
      LineOne: '',
      LineTwo: '',
      Suburb: '',
      State: '',
      Postcode: '',
      Country: 'Australia',
    },
    IndividualHasDifferentResidentialAddress: '',
    IndividualResidentialAddress: {
      SearchText: '',
      Address: '',
      LineOne: '',
      LineTwo: '',
      Suburb: '',
      State: '',
      Postcode: '',
      Country: 'Australia',
    },
    // Step 12 Individual Contact
    IndividualContactNumber: {
      ContactNumber: '',
    },
    IndividualHasNoEmail: '',
    IndividualEmail: '',
    IndividualContactNumberWarning: '',
    IndividualEmailWarning: '',
    // Step 12 Consent Questions
    // IndividualEmailTo:'';
    IndividualReceiveDocs: '',

    // Step 12 Company
    CompanyGivenName: '',
    CompanyFamilyName: '',
    PositionInCompany: '',
    CompanyContactNumber: {
      ContactNumber: '',
    },
    CompanyHasNoEmail: '',
    CompanyEmail: '',
    CompanyReceiveDocs: '',
    CompanyContactNumberWarning: '',
    CompanyEmailWarning: '',
    // CompanyEmailTo: '';
    CompanyName: '',
    AbnOrAcnType: '',
    AbnNo: '',
    AcnNo: '',
    CompanyStreetAddress: {
      SearchText: '',
      Address: '',
      LineOne: '',
      LineTwo: '',
      Suburb: '',
      State: '',
      Postcode: '',
      Country: 'Australia',
    },
    CompanyHasDifferentPostalAddress: '',
    CompanyPostalAddress: {
      SearchText: '',
      Address: '',
      LineOne: '',
      LineTwo: '',
      Suburb: '',
      State: '',
      Postcode: '',
      Country: 'Australia',
    },
    declaration: false,
    // Step 10
    submissionNo: '',
    FormExperience: '',
    // Submission Error
    submissionError: '',

    // Partial Forms
    ID: '',
    PartialFormID: '',
    Obligations: [],
    FormData: {
      a: '',
      submitted: false,
      OBLIGATION_NUMBER: '',
      isDebtorTypeCompany: null,
      isDebtorTypeIndividual: null,
      // Step 3
      OFFENCE_DATE: null,
      HEARING_DATE: null,
      DRIVER_LICENCE: '',
      VEHICLE_REGISTRATION: '',
      DOB: null,
      // Step 4
      SaveYesNo: '',
      SaveOptions: '',
      saveEmail: '',
      saveMobile: '',
      // Step 5
      SelectedObligationsStep5: [],
      TotalPaymentArrangementAmount: '',
      TotalPaymentArrangementAmountDisplay: '',
      AuthorisedObligation: {} as iObligation,
      InfringementNoticeNumber: '',
      IneligibleObligations: [],
      EligibleObligations: [],
      ArrangedObligations: {} as { [PANumber: string]: iObligation[] },
      AdditionalObligationsNum: 0,
      // Step 6
      MoreInfoAboutMatters: [],
      NoInfoApplies: false,
      // Step 7
      SupportingDocumentsUpfrontPayment: [],
      DetailsForUpfrontPaymentCircumstances: '',
      SupportingDocUpfrontPaymentNameField: '',
      SupportingDocUpfrontPaymentFileUploadErrorHandle: false,
      SupportingDocUpfrontPaymentUploadFileSectionError: '',
      SupportingDocUpfrontPaymentFilesProvider: '',
      SupportingDocUpfrontPaymentFilesDescription: '',
      SupportingDocUpfrontPaymentFieldErrorHandle: false,
      isStep7ContinueApplicationClicked: false,

      // Step 8
      receiveCenterlinkPayments: '',
      centrelinkPaymentType: '',
      instalmentDeducted: '',
      specifyPaymentType: '',
      centrelinkRefNo: '',
      fortnightlyPaymentAmount: '',
      // centrelinkCheck: false,

      // Step 9
      instalmentFrequency: '',
      instalmentAmount: '',
      instalmentNumberOfPayments: '',
      firstInstalmentDate: dateAfter21Days,

      // Step 10
      haveExceptionalCircumstances: '',
      SupportingFinancialDocuments: [],
      DetailsOfFinancialCircumstances: '',
      SupportingFinancialDocNameField: '',
      SupportingFinancialDocFileUploadErrorHandle: false,
      SupportingFinancialDocUploadFileSectionError: '',
      SupportingFinancialDocFilesProvider: '',
      SupportingFinancialDocFilesDescription: '',
      SupportingFinancialDocFieldErrorHandle: false,
      numberOfDependents: '',
      FileSizeLimitFinancialDocs: '4',

      // Step 11
      fortnightlyIncomeAfterTax: '',
      fortnightlyExpenses: '',
      quarterlyProfit: '',
      quarterlyLoss: '',

      // Step 12
      IndividualFamilyName: '',
      IndividualGivenName: '',
      // Step 12 Individual License
      LicenceState: '',
      NoDriverLicence: false,
      StatecountryOfIssue: 'Australia',
      // Step 12 Residential Address
      IndividualPostalAddress: {
        SearchText: '',
        Address: '',
        LineOne: '',
        LineTwo: '',
        Suburb: '',
        State: '',
        Postcode: '',
        Country: 'Australia',
      },
      IndividualHasDifferentResidentialAddress: '',
      IndividualResidentialAddress: {
        SearchText: '',
        Address: '',
        LineOne: '',
        LineTwo: '',
        Suburb: '',
        State: '',
        Postcode: '',
        Country: 'Australia',
      },
      // Step 12 Individual Contact
      IndividualContactNumber: {
        ContactNumber: '',
      },
      IndividualHasNoEmail: '',
      IndividualEmail: '',
      IndividualContactNumberWarning: '',
      IndividualEmailWarning: '',
      // Step 12 Consent Questions
      // IndividualEmailTo:'';
      IndividualReceiveDocs: '',

      // Step 12 Company
      CompanyGivenName: '',
      CompanyFamilyName: '',
      PositionInCompany: '',
      CompanyContactNumber: {
        ContactNumber: '',
      },
      CompanyHasNoEmail: '',
      CompanyEmail: '',
      CompanyReceiveDocs: '',
      CompanyContactNumberWarning: '',
      CompanyEmailWarning: '',
      // CompanyEmailTo: '';
      CompanyName: '',
      AbnOrAcnType: '',
      AbnNo: '',
      AcnNo: '',
      CompanyStreetAddress: {
        SearchText: '',
        Address: '',
        LineOne: '',
        LineTwo: '',
        Suburb: '',
        State: '',
        Postcode: '',
        Country: 'Australia',
      },
      CompanyHasDifferentPostalAddress: '',
      CompanyPostalAddress: {
        SearchText: '',
        Address: '',
        LineOne: '',
        LineTwo: '',
        Suburb: '',
        State: '',
        Postcode: '',
        Country: 'Australia',
      },
      declaration: false,
    }
  };
  return defaultValues;
};

const dateAfter84Days = new Date();
dateAfter84Days.setDate(new Date().getDate() + 84);

export const InstalmentSchema = Yup.object().shape({
  saveEmail: Yup.string().matches(EMAIL_SPECIAL_CHARS_REGEX, INVALID_EMAIL).email(INVALID_EMAIL).required(INVALID_NUMERIC_FORMAT),
  saveMobile: Yup.string()
    .min(10, INVALID_MOBILE_NUMBER_FORMAT)
    .max(10, INVALID_PHONE_NUMBER_LENGTH)
    .matches(CONTACT_NUMBER_REGEX, INVALID_PHONE_NUMBER)
    .required(INVALID_PHONE_NUMBER),
  SaveYesNo: Yup.string().required(INVALID_YESNO_FORMAT),
  SaveOptions: Yup.string().when('SaveYesNo', {
    is: 'true',
    then: Yup.string().required(INVALID_NUMERIC_FORMAT),
  }),
  OBLIGATION_NUMBER: Yup.string().matches(OBLIGATION_REGEX, INVALID_OBLIGATION_NUMBER).required(INVALID_NUMERIC_FORMAT),
  OFFENCE_DATE: Yup.date().required(EMPTY_DATE).nullable().max(new Date(), INVALID_FUTURE_DATE),
  HEARING_DATE: Yup.date().required(EMPTY_DATE).nullable(),
  DRIVER_LICENCE: Yup.string()
    .required(INVALID_NUMERIC_FORMAT)
    .matches(/^[a-zA-Z0-9 ]*$/, INVALID_LICENCE_FORMAT),
  VEHICLE_REGISTRATION: Yup.string().matches(VEHICLE_REGISTRATION_REGEX, INVALID_VEHICLE_REGISTRATION_NUMBER).required(INVALID_VEHICLE_REGISTRATION_NUMBER),
  DOB: Yup.date().required(EMPTY_DATE).nullable().max(new Date(), INVALID_FUTURE_DATE),
  TotalPaymentArrangementAmount: Yup.string(),
  MoreInfoAboutMatters: Yup.array().min(1, EMPTY_CHECKBOX),
  NoInfoApplies: Yup.boolean().oneOf([true], EMPTY_CHECKBOX).required(EMPTY_CHECKBOX),
  // Step 7
  DetailsForUpfrontPaymentCircumstances: Yup.string().matches(FREE_TEXT_FIELD_REGEX, INVALID_FREE_TEXT_FIELD_FORMAT),
  FileSizeLimitUpfrontPayment: Yup.string(),
  SupportingDocUpfrontPaymentFieldErrorHandle: Yup.boolean().test('fileSizeTooLarge',
  function (value) {
    const { FileSizeLimitUpfrontPayment } = this.parent;
    if (value === true) {
      return this.createError({
        message: INVALID_FILE_SIZE_CUSTOM(FileSizeLimitUpfrontPayment)
      });
    }
    return true;
  }),
  SupportingDocUpfrontPaymentFileUploadErrorHandle: Yup.boolean().oneOf([false], FILE_UPLOAD_FAIL),
  SupportingDocUpfrontPaymentUploadFileSectionError: Yup.boolean().when('SupportingFinancialDocuments', {
    is: (SupportingDocuments: []) => SupportingDocuments.length < 1,
    then: Yup.boolean(),
  }),
  // Step 8
  receiveCenterlinkPayments: Yup.string().required(INVALID_YESNO_FORMAT),
  centrelinkPaymentType: Yup.string().required(INVALID_YESNO_FORMAT),
  instalmentDeducted: Yup.string().required(INVALID_YESNO_FORMAT),
  specifyPaymentType: Yup.string().matches(FREE_TEXT_FIELD_REGEX, INVALID_FREE_TEXT_FIELD_FORMAT).max(40, INVALID_INPUT_LONGER_THAN_40_CHARS).required(INVALID_NUMERIC_FORMAT),
  centrelinkRefNo: Yup.string().matches(CRN_REGEX, INVALID_CRN).required(INVALID_NUMERIC_FORMAT),
  fortnightlyPaymentAmount: Yup.string().matches(ONLY_NUMBERS_REGEX, INVALID_MONETARY_AMOUNT)
    .test('isNum', INVALID_MONETARY_AMOUNT, (value) => !Number.isNaN(Number(value)))
    .test('lessThan10', INVALID_MIN_PAYMENT_FORTNIGHTLY, (value) => Number(value) >= 10)
    .required(INVALID_NUMERIC_FORMAT),
  // centrelinkCheck: Yup.boolean().oneOf([true], MISSING_CENTRELINK_CHECK),

  // Step 9
  instalmentFrequency: Yup.string().required(INVALID_YESNO_FORMAT),
  instalmentAmount: Yup.string()
    .when('instalmentFrequency', {
      is: 'fortnightly',
      then:
      Yup.string()
      .test('isNum', INVALID_MONETARY_AMOUNT, (value) => !Number.isNaN(Number(value)))
      .test('lessThan10', INVALID_MIN_PAYMENT_LOW, (value) => Number(value) >= 10)
      .test('isTooHigh', INVALID_MIN_PAYMENT_HIGH, function (value) {
        const { TotalPaymentArrangementAmount } = this.parent;
        return value !== undefined ? parseFloat(value) <= parseFloat(TotalPaymentArrangementAmount) : false;
      })
      .test('repaymentsTakeTooLong',
        function (value) {
        const { TotalPaymentArrangementAmount } = this.parent;
        if (value !== undefined && (Math.ceil((parseFloat(TotalPaymentArrangementAmount) / (parseFloat(value)))) > 260)) {
          // Find Minimum payments
          const minimumFortnightlyPayments = Math.ceil(parseFloat(TotalPaymentArrangementAmount) / (10 * 24));
          const minimumMonthlyPayments = Math.ceil(parseFloat(TotalPaymentArrangementAmount) / (10 * 12));
          return this.createError({
            message: INVALID_REPAYMENT_TIME_LENGTH(minimumFortnightlyPayments.toString(), minimumMonthlyPayments.toString())
          });
        }
        return true;
      })
    })
    .when('instalmentFrequency', {
      is: 'monthly',
      then: Yup.string().test('isNum', INVALID_MONETARY_AMOUNT, (value) => !Number.isNaN(Number(value)))
      .test('lessThan20', INVALID_MIN_PAYMENT_LOW, (value) => Number(value) >= 20)
      .test('isTooHigh', INVALID_MIN_PAYMENT_HIGH, function (value) {
        const { TotalPaymentArrangementAmount } = this.parent;
        return value !== undefined ? parseFloat(value) <= parseFloat(TotalPaymentArrangementAmount) : false;
      })
      .test('repaymentsTakeTooLong',
        function (value) {
        const { TotalPaymentArrangementAmount } = this.parent;
        if (value !== undefined && (Math.ceil((parseFloat(TotalPaymentArrangementAmount) / (parseFloat(value)))) > 120)) {
          // Find Minimum payments
          const minimumFortnightlyPayments = Math.ceil(parseFloat(TotalPaymentArrangementAmount) / (10 * 24));
          const minimumMonthlyPayments = Math.ceil(parseFloat(TotalPaymentArrangementAmount) / (10 * 12));
          return this.createError({
            message: INVALID_REPAYMENT_TIME_LENGTH(minimumFortnightlyPayments.toString(), minimumMonthlyPayments.toString())
          });
        }
        return true;
      })
    })
    .required(INVALID_NUMERIC_FORMAT),
  firstInstalmentDate: Yup.date()
    .min(today, INVALID_FIRST_INSTALMENT_DATE(dateAfter84Days))
    .max(dateAfter84Days, INVALID_FIRST_INSTALMENT_DATE(dateAfter84Days))
    .nullable()
    .required(EMPTY_DATE),

  // Step 10
  haveExceptionalCircumstances: Yup.string().required(INVALID_YESNO_FORMAT),
  DetailsOfFinancialCircumstances: Yup.string().matches(FREE_TEXT_FIELD_REGEX, INVALID_FREE_TEXT_FIELD_FORMAT).required(INVALID_NUMERIC_FORMAT),
  FileSizeLimitFinancialDocs: Yup.string(),
  SupportingFinancialDocFieldErrorHandle: Yup.boolean().test('repaymentsTakeTooLong',
  function (value) {
    const { FileSizeLimitFinancialDocs } = this.parent;
    if (value === true) {
      return this.createError({
        message: INVALID_FILE_SIZE_CUSTOM(FileSizeLimitFinancialDocs)
      });
    }
    return true;
  }),
  SupportingFinancialDocFileUploadErrorHandle: Yup.boolean().oneOf([false], FILE_UPLOAD_FAIL),
  SupportingFinancialDocUploadFileSectionError: Yup.boolean().when('SupportingFinancialDocuments', {
    is: (SupportingDocuments: []) => SupportingDocuments.length < 1,
    then: Yup.boolean(),
  }),
  numberOfDependents: Yup.string().matches(ONLY_NUMBERS_REGEX, INVALID_NUMBER_INPUT).max(2, INVALID_NUMBER_INPUT),

  // Step 11
  fortnightlyIncomeAfterTax: Yup.string().matches(ONLY_NUMBERS_REGEX, INVALID_MONETARY_AMOUNT).max(10, INVALID_MONETARY_AMOUNT),
  fortnightlyExpenses: Yup.string().matches(ONLY_NUMBERS_REGEX, INVALID_MONETARY_AMOUNT).max(10, INVALID_MONETARY_AMOUNT),
  quarterlyProfit: Yup.string().matches(ONLY_NUMBERS_REGEX, INVALID_MONETARY_AMOUNT),
  quarterlyLoss: Yup.string().matches(ONLY_NUMBERS_REGEX, INVALID_MONETARY_AMOUNT),

  // Step 12
  IndividualFamilyName: Yup.string().matches(NAMES_FIELDS_REGEX, INVALID_NAME_FORMAT_NEW).max(80, INVALID_INPUT_LONGER_THAN_80_CHARS).required(INVALID_NUMERIC_FORMAT),
  IndividualGivenName: Yup.string().matches(NAMES_FIELDS_REGEX, INVALID_NAME_FORMAT_NEW).max(80, INVALID_INPUT_LONGER_THAN_80_CHARS).required(INVALID_NUMERIC_FORMAT),
  StatecountryOfIssue: Yup.string(),
  LicenceState: Yup.string().required(INVALID_NUMERIC_FORMAT),
  NoDriverLicence: Yup.boolean(),
  IndividualPostalAddress: Yup.object().shape({
    SearchText: Yup.string().matches(ADDRESS_FIELD_CHARS_REGEX, INVALID_ADDRESS_SEARCH_FORMAT).max(200, INVALID_INPUT_LONGER_THAN_200_CHARS),
    LineOne: Yup.string().matches(ADDRESS_FIELD_CHARS_REGEX, INVALID_ADDRESS_FIELD_ERROR).max(200, INVALID_INPUT_LONGER_THAN_200_CHARS).required(INVALID_NUMERIC_FORMAT),
    LineTwo: Yup.string().matches(ADDRESS_FIELD_CHARS_REGEX, INVALID_ADDRESS_FIELD_ERROR).max(200, INVALID_INPUT_LONGER_THAN_200_CHARS),
    Suburb: Yup.string().matches(ADDRESS_FIELD_CHARS_REGEX, INVALID_ADDRESS_FIELD_ERROR).max(50, INVALID_INPUT_LONGER_THAN_50_CHARS).required(INVALID_NUMERIC_FORMAT),
    Postcode: Yup.string().matches(POST_CODE_REGEX, INVALID_POST_CODE).max(10, INVALID_INPUT_LONGER_THAN_10_CHARS).required(INVALID_NUMERIC_FORMAT),
    Country: Yup.string().max(50, INVALID_INPUT_LONGER_THAN_50_CHARS).required(INVALID_NUMERIC_FORMAT),
    State: Yup.string().when('Country', {
      is: 'Australia',
      then: Yup.string().matches(ADDRESS_FIELD_CHARS_REGEX, INVALID_ADDRESS_FIELD_ERROR).max(100, INVALID_INPUT_LONGER_THAN_100_CHARS).required(INVALID_NUMERIC_FORMAT),
    }),
  }),
  IndividualHasDifferentResidentialAddress: Yup.string().required(INVALID_YESNO_FORMAT).nullable(),
  IndividualResidentialAddress: Yup.object().shape({
    SearchText: Yup.string().matches(ADDRESS_FIELD_CHARS_REGEX, INVALID_ADDRESS_SEARCH_FORMAT).max(200, INVALID_INPUT_LONGER_THAN_200_CHARS),
    LineOne: Yup.string().matches(ADDRESS_FIELD_CHARS_REGEX, INVALID_ADDRESS_FIELD_ERROR).max(200, INVALID_INPUT_LONGER_THAN_200_CHARS).required(INVALID_NUMERIC_FORMAT),
    LineTwo: Yup.string().matches(ADDRESS_FIELD_CHARS_REGEX, INVALID_ADDRESS_FIELD_ERROR).max(200, INVALID_INPUT_LONGER_THAN_200_CHARS),
    Suburb: Yup.string().matches(ADDRESS_FIELD_CHARS_REGEX, INVALID_ADDRESS_FIELD_ERROR).max(50, INVALID_INPUT_LONGER_THAN_50_CHARS).required(INVALID_NUMERIC_FORMAT),
    Postcode: Yup.string().matches(POST_CODE_REGEX, INVALID_POST_CODE).max(10, INVALID_POST_CODE).required(INVALID_NUMERIC_FORMAT),
    Country: Yup.string().max(50, INVALID_INPUT_LONGER_THAN_50_CHARS).required(INVALID_NUMERIC_FORMAT),
    State: Yup.string().when('Country', {
      is: 'Australia',
      then: Yup.string().matches(ADDRESS_FIELD_CHARS_REGEX, INVALID_ADDRESS_FIELD_ERROR).max(100, INVALID_INPUT_LONGER_THAN_100_CHARS).required(INVALID_NUMERIC_FORMAT),
    }),
  }),
  DeliveryAddressType: Yup.string().required(INVALID_YESNO_FORMAT),
  IndividualEmail: Yup.string()
    .matches(EMAIL_SPECIAL_CHARS_REGEX, INVALID_EMAIL)
    .email(INVALID_EMAIL)
    .max(100, INVALID_EMAIL)
    .required(INVALID_NUMERIC_FORMAT),
  IndividualHasNoEmail: Yup.boolean(),
  IndividualContactNumber: Yup.object().shape({
    ContactNumber: Yup.string().max(15, INVALID_PHONE_NUMBER_LENGTH_15).matches(CONTACT_NUMBER_REGEX, INVALID_PHONE_NUMBER).required(INVALID_PHONE_NUMBER),
  }),
  // IndividualEmailTo: Yup.string().required(INVALID_YESNO_FORMAT).nullable(),
  IndividualReceiveDocs: Yup.string().required(INVALID_YESNO_FORMAT).nullable(),

  CompanyGivenName: Yup.string().matches(NAMES_FIELDS_REGEX, INVALID_NAME_FORMAT_NEW).max(80, INVALID_INPUT_LONGER_THAN_80_CHARS).required(INVALID_NUMERIC_FORMAT),
  CompanyFamilyName: Yup.string().matches(NAMES_FIELDS_REGEX, INVALID_NAME_FORMAT_NEW).max(80, INVALID_INPUT_LONGER_THAN_80_CHARS).required(INVALID_NUMERIC_FORMAT),
  PositionInCompany: Yup.string().matches(POSITION_IN_COMPANY_REGEX, INVALID_POSITION_IN_COMPANY_FIELD).max(80, INVALID_INPUT_LONGER_THAN_80_CHARS).required(INVALID_NUMERIC_FORMAT),
  CompanyContactNumber: Yup.object().shape({
    ContactNumber: Yup.string().max(15, INVALID_PHONE_NUMBER_LENGTH_15).matches(CONTACT_NUMBER_REGEX, INVALID_PHONE_NUMBER).required(INVALID_PHONE_NUMBER),
  }),
  CompanyHasNoEmail: Yup.boolean(),
  CompanyEmail: Yup.string()
    .matches(EMAIL_SPECIAL_CHARS_REGEX, INVALID_EMAIL)
    .email(INVALID_EMAIL)
    .max(100, INVALID_EMAIL)
    .required(INVALID_NUMERIC_FORMAT),
  CompanyReceiveDocs: Yup.string().required(INVALID_YESNO_FORMAT).nullable(),
  // CompanyEmailTo: Yup.string().required(INVALID_YESNO_FORMAT).nullable(),
  CompanyName: Yup.string().matches(COMPANY_NAME_REGEX, INVALID_COMPANY_NAME).max(75, INVALID_INPUT_LONGER_THAN_75_CHARS).required(INVALID_NUMERIC_FORMAT),
  AbnOrAcnType: Yup.string().required(INVALID_NUMERIC_FORMAT),
  AbnNo: Yup.string().required(INVALID_NUMERIC_FORMAT),
  AcnNo: Yup.string().required(INVALID_NUMERIC_FORMAT),
  CompanyStreetAddress: Yup.object().shape({
    SearchText: Yup.string().matches(ADDRESS_FIELD_CHARS_REGEX, INVALID_ADDRESS_SEARCH_FORMAT).max(200, INVALID_INPUT_LONGER_THAN_200_CHARS),
    LineOne: Yup.string().matches(ADDRESS_FIELD_CHARS_REGEX, INVALID_ADDRESS_FIELD_ERROR).max(200, INVALID_INPUT_LONGER_THAN_200_CHARS).required(INVALID_NUMERIC_FORMAT),
    LineTwo: Yup.string().matches(ADDRESS_FIELD_CHARS_REGEX, INVALID_ADDRESS_FIELD_ERROR).max(200, INVALID_INPUT_LONGER_THAN_200_CHARS),
    Suburb: Yup.string().matches(ADDRESS_FIELD_CHARS_REGEX, INVALID_ADDRESS_FIELD_ERROR).max(50, INVALID_INPUT_LONGER_THAN_50_CHARS).required(INVALID_NUMERIC_FORMAT),
    Postcode: Yup.string().matches(POST_CODE_REGEX, INVALID_POST_CODE).max(10, INVALID_POST_CODE).required(INVALID_NUMERIC_FORMAT),
    Country: Yup.string().max(50, INVALID_INPUT_LONGER_THAN_50_CHARS).required(INVALID_NUMERIC_FORMAT),
    State: Yup.string().when('Country', {
      is: 'Australia',
      then: Yup.string().matches(ADDRESS_FIELD_CHARS_REGEX, INVALID_ADDRESS_FIELD_ERROR).max(100, INVALID_INPUT_LONGER_THAN_100_CHARS).required(INVALID_NUMERIC_FORMAT),
    }),
  }),
  CompanyHasDifferentPostalAddress: Yup.string().required(INVALID_YESNO_FORMAT).nullable(),
  CompanyPostalAddress: Yup.object().shape({
    SearchText: Yup.string().matches(ADDRESS_FIELD_CHARS_REGEX, INVALID_ADDRESS_SEARCH_FORMAT).max(200, INVALID_INPUT_LONGER_THAN_200_CHARS),
    LineOne: Yup.string().matches(ADDRESS_FIELD_CHARS_REGEX, INVALID_ADDRESS_FIELD_ERROR).max(200, INVALID_INPUT_LONGER_THAN_200_CHARS).required(INVALID_NUMERIC_FORMAT),
    LineTwo: Yup.string().matches(ADDRESS_FIELD_CHARS_REGEX, INVALID_ADDRESS_FIELD_ERROR).max(200, INVALID_INPUT_LONGER_THAN_200_CHARS),
    Suburb: Yup.string().matches(ADDRESS_FIELD_CHARS_REGEX, INVALID_ADDRESS_FIELD_ERROR).max(50, INVALID_INPUT_LONGER_THAN_50_CHARS).required(INVALID_NUMERIC_FORMAT),
    Postcode: Yup.string().matches(POST_CODE_REGEX, INVALID_POST_CODE).max(10, INVALID_POST_CODE).required(INVALID_NUMERIC_FORMAT),
    Country: Yup.string().max(50, INVALID_INPUT_LONGER_THAN_50_CHARS).required(INVALID_NUMERIC_FORMAT),
    State: Yup.string().when('Country', {
      is: 'Australia',
      then: Yup.string().matches(ADDRESS_FIELD_CHARS_REGEX, INVALID_ADDRESS_FIELD_ERROR).max(100, INVALID_INPUT_LONGER_THAN_100_CHARS).required(INVALID_NUMERIC_FORMAT),
    }),
  }),
  declaration: Yup.boolean().oneOf([true], MISSING_DECLARATION_AGREEMENT),
});

export const InstalmentMetaData: iFormMetaData = {
  pagesSegments: {
    step0: {
      progressStep: ProgressStep.START,
      validationKeys: () => [],
    },
    step1: {
      progressStep: ProgressStep.START,
      validationKeys: (values: FormikValues) => keys(values, INSTALMENT).page1,
    },
    step2: {
      progressStep: ProgressStep.START,
      validationKeys: (values: FormikValues) => keys(values, INSTALMENT).page2,
    },
    step3: {
      progressStep: ProgressStep.START,
      validationKeys: (values: FormikValues) => keys(values, INSTALMENT).page3,
    },
    step4: {
      progressStep: ProgressStep.START,
      validationKeys: (values: FormikValues) => keys(values, INSTALMENT).page4,
    },
    step5: {
      progressStep: ProgressStep.DETAILS,
      validationKeys: (values: FormikValues) => keys(values, INSTALMENT).page5,
    },
    step6: {
      progressStep: ProgressStep.DETAILS,
      validationKeys: (values: FormikValues) => keys(values, INSTALMENT).page6,
    },
    step7: {
      progressStep: ProgressStep.DETAILS,
      validationKeys: (values: FormikValues) => keys(values, INSTALMENT).page7,
    },
    step8: {
      progressStep: ProgressStep.DETAILS,
      validationKeys: (values: FormikValues) => keys(values, INSTALMENT).page8,
    },
    step9: {
      progressStep: ProgressStep.DETAILS,
      validationKeys: (values: FormikValues) => keys(values, INSTALMENT).page9,
    },
    step10: {
      progressStep: ProgressStep.DETAILS,
      validationKeys: (values: FormikValues) => keys(values, INSTALMENT).page10,
    },
    step11: {
      progressStep: ProgressStep.DETAILS,
      validationKeys: (values: FormikValues) => keys(values, INSTALMENT).page11,
    },
    step12: {
      progressStep: ProgressStep.DETAILS,
      validationKeys: (values: FormikValues) => keys(values, INSTALMENT).page12,
    },
    step13: {
      progressStep: ProgressStep.CHECK_CONFIRM,
      validationKeys: (values: FormikValues) => keys(values, INSTALMENT).page13,
    },
    step14: {
      progressStep: ProgressStep.CHECK_CONFIRM,
      validationKeys: (values: FormikValues) => keys(values, INSTALMENT).page14,
    },
    step15: {
      progressStep: ProgressStep.DONE,
      validationKeys: (values: FormikValues) => keys(values, INSTALMENT).page14,
    },
  },
};

const displayFileDetails = (attachment: iAttachments) => {
  if (attachment.FileName !== '' && attachment.FileDescription !== '') {
    return `${attachment.FileName} : ${attachment.FileDescription}`;
  }
  if (attachment.FileName !== '' && attachment.FileDescription === '') {
    return `${attachment.FileName}`;
  }
  return '';
};
const joinAddressFields = (entry: string[]) => {
  if (entry[0] === 'Address' || entry[0] === 'SearchText') return null;
  if (entry[0] === 'Country') return entry[1];
  return entry[1];
};

export const getSummaryScreen = (values: iInstalmentFormValues): iSummary[] => {
  const checkFines = values.SelectedObligationsStep5.map((fine) => {
    if (!fine.checked) return fine.number.toString();
    return '';
  })
    .filter(Boolean)
    .join('; ');
    const page5 = {
      title: 'Your fines',
      editLink: '/step5',
      fields: [
        [{
          title: 'All of your fines have been included in this application.',
          value: '',
        }],
        checkFines !== '' ? [{
          title: 'Any fines that are NOT included are listed below:',
          value: checkFines !== '' ? `${checkFines}` : '',
        }] : [],
        values.IneligibleObligations.length > 0 ? [
          {
            title: '',
            value: `You also have ${values.IneligibleObligations.length} fines that are not eligible to be included on a payment arrangement. Please contact Fines Victoria to pay or to discuss any options you may have.`
          }
        ] : []
      ]
    };
  const page6 = {
    title: 'More information about your fines',
    editLink: '/step6',
    fields: [
      [{ title: 'Select the statement(s) that apply to you:', value: values.MoreInfoAboutMatters.join('\r\n') }]
    ]
  };
  const page7 = {
    title: 'Upfront payment required',
    editLink: '/step7',
    fields: [
      values.DetailsForUpfrontPaymentCircumstances !== ''
      ? [{ title: 'If you continue with this online application, we will contact you to make an upfront payment. If you cannot afford the upfront payment, please provide the reasons and attach any supporting evidence.', value: values.DetailsForUpfrontPaymentCircumstances }]
      : [],
      values.SupportingDocumentsUpfrontPayment.length > 0
      ? [{
        title: 'Attachments to be included in this application',
        value: values.SupportingDocumentsUpfrontPayment.map((attachment) => displayFileDetails(attachment))
          .filter(Boolean)
          .join('\n'),
      }] : [],
    ]
  };

  const page8 = {
    title: 'Centrelink',
    editLink: '/step8',
    fields: [
      [{ title: 'Do you receive any Centrelink payments?', value: values.receiveCenterlinkPayments === 'true' ? 'Yes' : 'No' }],
      values.receiveCenterlinkPayments === 'true' ? [{ title: 'Would you like your instalments deducted from your Centrelink payments via Centrepay?', value: values.instalmentDeducted === 'true' ? 'Yes' : 'No' }] : [],
      values.receiveCenterlinkPayments === 'true' && values.instalmentDeducted === 'true' ? [{ title: 'What type of Centrelink payment do you receive?', value: values.centrelinkPaymentType }] : [],
      values.receiveCenterlinkPayments === 'true' && values.instalmentDeducted === 'true' && values.centrelinkPaymentType === 'Other' ? [{ title: 'Please specify', value: values.specifyPaymentType }] : [],
      values.receiveCenterlinkPayments === 'true' && values.instalmentDeducted === 'true' ? [{ title: 'Centrelink Reference Number (CRN)', value: values.centrelinkRefNo }] : [],
      values.receiveCenterlinkPayments === 'true' && values.instalmentDeducted === 'true' ? [{ title: 'How much can you pay fortnightly?', value: `$ ${values.fortnightlyPaymentAmount}` }] : [],
    ]
  };
  const page9 = {
    title: 'Maximum amount you can pay',
    editLink: '/step9',
    fields: [
      [{ title: 'How often would you like to pay each instalment?', value: values.instalmentFrequency.charAt(0).toUpperCase() + values.instalmentFrequency.slice(1) }],
      [{ title: 'How much can you pay each instalment?', value: `$ ${values.instalmentAmount}` }],
      [{ title: 'When will your first instalment payment occur?', value: values.firstInstalmentDate ? `${`${`0${values.firstInstalmentDate.getDate()}`.slice(-2)}/${`0${values.firstInstalmentDate.getMonth() + 1}`.slice(-2)}`}/${values.firstInstalmentDate.getFullYear()}` : 'n/a' }],
    ]
  };
  const page10 = {
    title: 'Your financial details',
    editLink: '/step10',
    fields: [
      [{ title: 'Do you have any exceptional financial circumstances you would like to let us know about?', value: values.haveExceptionalCircumstances === 'true' ? 'Yes' : 'No' }],
      (values.haveExceptionalCircumstances === 'true' && values.DetailsOfFinancialCircumstances !== '')
      ? [{ title: 'Provide details for any circumstances you would like to be considered in this application.', value: values.DetailsOfFinancialCircumstances }]
      : [],
      (values.haveExceptionalCircumstances === 'true' && values.SupportingFinancialDocuments.length > 0)
      ? [{
        title: 'Attachments to be included in this application',
        value: values.SupportingFinancialDocuments.map((attachment) => displayFileDetails(attachment))
          .filter(Boolean)
          .join('\n'),
      }] : [],
      values.numberOfDependents !== ''
      ? [{
        title: 'How many dependants do you have?',
        value: values.numberOfDependents
      }] : [],
    ]
  };
  const page11 = {
    title: values.isDebtorTypeCompany ? 'Profit/Loss' : 'Income/expenses',
    editLink: '/step11',
    fields: [
      values.isDebtorTypeCompany ? [{ title: 'Profit', value: `$ ${values.quarterlyProfit}` }] : [{ title: 'Your fortnightly income (after tax)', value: `$ ${values.fortnightlyIncomeAfterTax}` }],
      values.isDebtorTypeCompany ? [{ title: 'Loss', value: `$ ${values.quarterlyLoss}` }] : [{ title: 'Your fortnightly expenses', value: `$ ${values.fortnightlyExpenses}` }]
    ]
  };
  const page12 = {
    title: values.isDebtorTypeCompany ? 'Details' : 'Your details',
    editLink: '/step12',
    fields: values.isDebtorTypeCompany ? [
      [{ title: 'Given name(s)', value: values.CompanyGivenName }],
      [{ title: 'Family name', value: values.CompanyFamilyName }],
      [{ title: 'Position in company', value: values.PositionInCompany }],
      [{ title: 'Contact number', value: values.CompanyContactNumber.ContactNumber }],
      [{ title: 'Email address', value: values.CompanyHasNoEmail ? 'N/A' : values.CompanyEmail }],
      // [{ title: 'Would you like to receive a copy of the completed application at the email address provided?', value: values.CompanyEmailTo === 'true' ? 'Yes' : 'No' }],
      [{ title: 'Do you consent to receiving correspondence about this application via email?', value: values.CompanyReceiveDocs === 'true' ? 'Yes' : 'No' }],
      [{ title: 'Company name', value: values.CompanyName }],
      [{ title: 'ABN/ACN', value: values.AbnOrAcnType }],
      [{ title: 'ABN', value: values.AbnOrAcnType === 'ABN' ? values.AbnNo : 'N/A' }],
      [{ title: 'ACN', value: values.AbnOrAcnType === 'ACN' ? values.AcnNo : 'N/A' }],
      [
          {
            title: "Company's street address",
            value: `${Object.entries(values.CompanyStreetAddress)
              .map((entry) => joinAddressFields(entry))
              .filter(Boolean)
              .join(', ')}`,
          },
        ],
        values.CompanyHasDifferentPostalAddress === 'false'
      ? [{ title: 'Is the postal address different to the street address?', value: 'No' }]
      : [
          {
            title: 'Postal address',
            value: `${Object.entries(values.CompanyPostalAddress)
              .map((entry) => joinAddressFields(entry))
              .filter(Boolean)
              .join(', ')}`,
          },
        ],
    ] : [
      [{ title: 'Given name(s)', value: values.IndividualGivenName }],
      [{ title: 'Family name', value: values.IndividualFamilyName }],
      [{ title: 'Date of birth', value: values.DOB ? `${`${`0${values.DOB.getDate()}`.slice(-2)}/${`0${values.DOB.getMonth() + 1}`.slice(-2)}`}/${values.DOB.getFullYear()}` : 'n/a' }],
      [
        values.NoDriverLicence
          ? { title: 'Driver licence number', value: 'n/a' }
          : { title: 'Driver licence number', value: values.DRIVER_LICENCE ? values.DRIVER_LICENCE : 'n/a' },
      ],
      [
          values.NoDriverLicence
            ? { title: 'Place of issue', value: 'n/a' }
            : {
                title: 'Place of issue',
                value:
                  // eslint-disable-next-line no-nested-ternary
                  values.StatecountryOfIssue === 'Australia' ? (values.LicenceState ? values.LicenceState : 'n/a') : values.StatecountryOfIssue ? values.StatecountryOfIssue : 'n/a',
              },
      ],
      [
          {
            title: 'Postal address',
            value: `${Object.entries(values.IndividualPostalAddress)
              .map((entry) => joinAddressFields(entry))
              .filter(Boolean)
              .join(', ')}`,
          },
        ],
        values.IndividualHasDifferentResidentialAddress === 'false'
      ? [{ title: 'Is your residential address different to your postal address?', value: 'No' }]
      : [
          {
            title: 'Residential address',
            value: `${Object.entries(values.IndividualResidentialAddress)
              .map((entry) => joinAddressFields(entry))
              .filter(Boolean)
              .join(', ')}`,
          },
        ],
        [{ title: 'Contact number', value: values.IndividualContactNumber.ContactNumber }],
        [{ title: 'Email address', value: values.IndividualHasNoEmail ? 'N/A' : values.IndividualEmail }],
      // [{ title: 'Would you like to receive a copy of the completed application at the email address provided?', value: values.IndividualEmailTo === 'true' ? 'Yes' : 'No' }],
        [{ title: 'Do you consent to receiving correspondence about this application via email?', value: values.IndividualReceiveDocs === 'true' ? 'Yes' : 'No' }],
    ]
  };

  const pagesExtensionSummary: iSummary[] = [
    page5,
    page6,
    (!values.NoInfoApplies && (values.DetailsForUpfrontPaymentCircumstances !== '' && values.SupportingDocumentsUpfrontPayment.length > 0)) ? page7 : {} as iSummary, // show it to sanctions only
    values.isDebtorTypeIndividual ? page8 : {} as iSummary, // show it to individuals only
    (values.isDebtorTypeIndividual && values.receiveCenterlinkPayments === 'true' && values.instalmentDeducted === 'true') ? {} as iSummary : page9,
    page10,
    (values.isDebtorTypeCompany && values.quarterlyLoss !== '' && values.quarterlyProfit !== '') || (values.isDebtorTypeIndividual && values.fortnightlyIncomeAfterTax !== '' && values.fortnightlyExpenses !== '') ? page11 : {} as iSummary,
    page12,
  ];
  const summarySections: iSummary[] = pagesExtensionSummary;
  return summarySections;
};

export interface iSubmitInstalmentFormValues extends iCommonInstalmentValues {
  YourObligationNumber: string;
  InfringementNoticeNumber: string;
  isIndividual: boolean;
  Sanction: string;

  // Details or Your Details
  FamilyName: string;
  GivenNames: string;

  // Company
  AbnacnType: string;
  AbnacnNo: string;
  PositionInCompany: string;
  CompanyName: string;

  // Comapny - Street Address
  LineOne: string;
  LineTwo: string;
  Suburb: string;
  State: string;
  Postcode: string;
  Country: string;

  // Comapny - Postal Address
  LineOneTwo: string;
  LineTwoTwo: string;
  SuburbTwo: string;
  StateTwo: string;
  PostcodeTwo: string;
  CountryTwo: string;

  // Residential Address
  ResidentialAddressLineOne: string;
  ResidentialAddressLineTwo: string;
  ResidentialAddressSuburb: string;
  ResidentialAddressState: string;
  ResidentialAddressPostcode: string;
  ResidentialAddressCountry: string;

  // Postal address
  PostalAddressLineOne: string;
  PostalAddressLineTwo: string;
  PostalAddressSuburb: string;
  PostalAddressState: string;
  PostalAddressCountry: string;

  YourEmailAddressOptional: string;
  DocumentElectronically: string;
  ContactNumber: string;
  DriverLicenceNumber: string;
  StateOrCountryOfIssue: string;
  CurrentLicence: boolean;

  // Date of Birth - Individual
  DOBDD: string;
  DOBMM: string;
  DOBYYYY: string;

  // Centrelink - Individual Specific
  DoYouReceiveAnyCentrelinkPayments: string;
  CentrelinkReferenceNumber: string;
  WhatTypeOfPaymentDoYouReceive: string;
  IfOther: string;
  CentrelinkYesOrNo: string;
  HowMuchCanYouPayFortnightly: string;

  FortnightlyCheckbox: boolean;
  CheckBoxSeven: boolean;
  MonthlyCheckbox: boolean;
  FortnightlyAmount: string;
  MonthlyAmount: string;
  // Individual first instalment date
  InstalmentDD: string;
  InstalmentMM: string;
  InstalmentYYYY: string;
  // Comapny first instalment date
  DdTwo: string;
  MmTwo: string;
  YyyyTwo: string;

  // Individual Income and Expenses
  YourFortnightlyIncome: string;
  NumberOfDependants: string;
  YourFortnightlyExpenses: string;

  // Company Profit and Loss
  ProfitOrLoss: string;

  DoYouHaveAnyExceptionalFinancialCircumstances: string;
  ProvideDetailsForAnyCircumstancesYouWouldLikeToBeConsideredForInThisApplication: string;
  ExpensesOfAttachments: string;

  StatementThatAppliesToYou: string;
  IfYouCannotAffordTheUpfrontPayment: string;
  MoreInformationOfAttachments: string;

  YouHaveChosenToNotIncludeTheFollowingFinesInThisApplication: string;

  ApplicantsSignature: string;
  // Individual
  DeclarationDD: string;
  DeclarationMM: string;
  DeclarationYYYY: string;
  // Company
  DDThree: string;
  MMThree: string;
  YYYYThree: string;

  FooterSubmissionNo1: string;
  FooterFormType1: string;
  FooterObligationNo1: string;
  FooterSubmittedTime1: string;
}

export const getSubmissionPayload = (formValues: iInstalmentFormValues): iSubmitInstalmentFormValues => {
  const submitFormValues = formValues;
  const now = new Date();
  const MONTH = (`0${now.getMonth() + 1}`).slice(-2);
  const DATE = (`0${now.getDate()}`).slice(-2);
  const YEAR = now.getFullYear();
  const MINUTE = (now.getMinutes() < 10 ? '0' : '') + now.getMinutes();
  const SUBMITTED_TIME = `${now.getHours()}:${MINUTE} ${DATE}/${MONTH}/${YEAR}`;
  const FORM_TYPE = 'Instalment';

  const totalNumberOfAttachmentsFinancial = formValues.SupportingFinancialDocuments?.length;
  const totalNumberOfAttachmentsUpfrontPayment = formValues.SupportingDocumentsUpfrontPayment?.length;
  const descriptionOfAttachmentsArrayFinancial = [
     ...(formValues.SupportingFinancialDocuments || []),
   ];
  let remoteAttachmentNames = '';
  if (descriptionOfAttachmentsArrayFinancial) {
    descriptionOfAttachmentsArrayFinancial.forEach((v) => {
      if (v.RemoteFileName) {
         remoteAttachmentNames += `${formValues.OBLIGATION_NUMBER}/${v.RemoteFileName}/${v.RemoteFileName}|`;
      }
    });
  }
  const descriptionOfAttachmentsArrayUpfrontPayment = [
    ...(formValues.SupportingDocumentsUpfrontPayment || []),
  ];
  let remoteAttachmentNames1 = '';
 if (descriptionOfAttachmentsArrayUpfrontPayment) {
  descriptionOfAttachmentsArrayUpfrontPayment.forEach((v) => {
     if (v.RemoteFileName) {
        remoteAttachmentNames1 += `${formValues.OBLIGATION_NUMBER}/${v.RemoteFileName}/${v.RemoteFileName}|`;
     }
   });
 }
 const allAttachmentNames = remoteAttachmentNames + remoteAttachmentNames1;
 const checkFines = formValues.SelectedObligationsStep5.map((fine) => {
  if (!fine.checked) return fine.number.toString();
  return '';
})
  .filter(Boolean)
  .join('; ');
  const submitObject: iSubmitInstalmentFormValues = {
    ...(submitFormValues as iCommonInstalmentValues),
    YourObligationNumber: formValues.OBLIGATION_NUMBER,
  InfringementNoticeNumber: formValues?.InfringementNoticeNumber,
  isIndividual: !!formValues.isDebtorTypeIndividual,
  Sanction: formValues.NoInfoApplies ? 'false' : 'true',

  // Details or Your Details
  FamilyName: formValues.isDebtorTypeIndividual ? formValues.IndividualFamilyName : formValues.CompanyFamilyName,
  GivenNames: formValues.isDebtorTypeIndividual ? formValues.IndividualGivenName : formValues.CompanyGivenName,

  // Company
  AbnacnType: formValues.AbnOrAcnType,
  AbnacnNo: formValues.AbnOrAcnType === 'ABN' ? formValues.AbnNo : formValues.AcnNo,
  PositionInCompany: formValues.PositionInCompany,
  CompanyName: formValues.CompanyName,

  // Comapny - Street Address
  LineOne: formValues.CompanyStreetAddress.LineOne,
  LineTwo: formValues.CompanyStreetAddress.LineTwo,
  Suburb: formValues.CompanyStreetAddress.Suburb,
  State: formValues.CompanyStreetAddress.State,
  Postcode: formValues.CompanyStreetAddress.Postcode,
  Country: formValues.CompanyStreetAddress.Country,

  // Comapny - Postal Address
  LineOneTwo: (formValues.CompanyHasDifferentPostalAddress === 'true' ? formValues.CompanyPostalAddress.LineOne : formValues.CompanyStreetAddress.LineOne),
  LineTwoTwo: (formValues.CompanyHasDifferentPostalAddress === 'true' ? formValues.CompanyPostalAddress.LineTwo : formValues.CompanyStreetAddress.LineTwo),
  SuburbTwo: (formValues.CompanyHasDifferentPostalAddress === 'true' ? formValues.CompanyPostalAddress.Suburb : formValues.CompanyStreetAddress.Suburb),
  StateTwo: (formValues.CompanyHasDifferentPostalAddress === 'true' ? formValues.CompanyPostalAddress.State : formValues.CompanyStreetAddress.State),
  CountryTwo: (formValues.CompanyHasDifferentPostalAddress === 'true' ? formValues.CompanyPostalAddress.Country : formValues.CompanyStreetAddress.Country),

  // Postal Address
  PostalAddressLineOne: formValues.IndividualPostalAddress.LineOne,
  PostalAddressLineTwo: formValues.IndividualPostalAddress.LineTwo,
  PostalAddressSuburb: formValues.IndividualPostalAddress.Suburb,
  PostalAddressState: formValues.IndividualPostalAddress.State,
  PostalAddressCountry: formValues.IndividualPostalAddress.Country,

  // eslint-disable-next-line no-nested-ternary
  PostcodeTwo: formValues.isDebtorTypeIndividual
  ? (formValues.IndividualPostalAddress.Postcode)
  : (formValues.CompanyHasDifferentPostalAddress === 'true' ? formValues.CompanyPostalAddress.Postcode : formValues.CompanyStreetAddress.Postcode),

  // Residential address
  ResidentialAddressLineOne: (formValues.IndividualHasDifferentResidentialAddress === 'true' ? formValues.IndividualResidentialAddress.LineOne : formValues.IndividualPostalAddress.LineOne),
  ResidentialAddressLineTwo: (formValues.IndividualHasDifferentResidentialAddress === 'true' ? formValues.IndividualResidentialAddress.LineTwo : formValues.IndividualPostalAddress.LineTwo),
  ResidentialAddressSuburb: (formValues.IndividualHasDifferentResidentialAddress === 'true' ? formValues.IndividualResidentialAddress.Suburb : formValues.IndividualPostalAddress.Suburb),
  ResidentialAddressState: (formValues.IndividualHasDifferentResidentialAddress === 'true' ? formValues.IndividualResidentialAddress.State : formValues.IndividualPostalAddress.State),
  ResidentialAddressPostcode: (formValues.IndividualHasDifferentResidentialAddress === 'true' ? formValues.IndividualResidentialAddress.Postcode : formValues.IndividualPostalAddress.Postcode),
  ResidentialAddressCountry: (formValues.IndividualHasDifferentResidentialAddress === 'true' ? formValues.IndividualResidentialAddress.Country : formValues.IndividualPostalAddress.Country),

  ATTACHMENTS: allAttachmentNames,
  // eslint-disable-next-line no-nested-ternary
  YourEmailAddressOptional: formValues.isDebtorTypeIndividual ? (!formValues.IndividualHasNoEmail ? formValues.IndividualEmail : '') : (!formValues.CompanyHasNoEmail ? formValues.CompanyEmail : ''),
  // eslint-disable-next-line no-nested-ternary
  EMAIL_TO: formValues.isDebtorTypeIndividual ? (!formValues.IndividualHasNoEmail ? formValues.IndividualEmail : '') : (!formValues.CompanyHasNoEmail ? formValues.CompanyEmail : ''),
  // eslint-disable-next-line no-nested-ternary
  DocumentElectronically: formValues.isDebtorTypeIndividual ? (formValues.IndividualReceiveDocs === 'true' ? 'Yes' : 'No') : (formValues.CompanyReceiveDocs === 'true' ? 'Yes' : 'No'),
  ContactNumber: formValues.isDebtorTypeIndividual ? formValues.IndividualContactNumber.ContactNumber : formValues.CompanyContactNumber.ContactNumber,
  DriverLicenceNumber: formValues.NoDriverLicence ? '' : formValues.DRIVER_LICENCE,
  // eslint-disable-next-line no-nested-ternary
  StateOrCountryOfIssue: formValues.NoDriverLicence ? '' : formValues.StatecountryOfIssue === 'Australia' ? formValues.LicenceState : formValues.StatecountryOfIssue,
  CurrentLicence: formValues.NoDriverLicence,

  // Date of Birth - Individual
  DOBDD: formValues.DOB ? (`0${formValues.DOB.getDate()}`).slice(-2) : '',
  DOBMM: formValues.DOB ? (`0${formValues.DOB.getMonth() + 1}`).slice(-2) : '',
  DOBYYYY: formValues.DOB ? formValues.DOB?.getFullYear().toString() : '',

  // Centrelink - Individual Specific
  // eslint-disable-next-line no-nested-ternary
  DoYouReceiveAnyCentrelinkPayments: formValues.isDebtorTypeIndividual ? formValues.receiveCenterlinkPayments === 'true' ? 'Yes' : 'No' : '',
  CentrelinkReferenceNumber: (formValues.isDebtorTypeIndividual && formValues.receiveCenterlinkPayments === 'true' && formValues.instalmentDeducted === 'true') ? formValues.centrelinkRefNo : '',
  WhatTypeOfPaymentDoYouReceive: (formValues.isDebtorTypeIndividual && formValues.receiveCenterlinkPayments === 'true' && formValues.instalmentDeducted === 'true') ? formValues.centrelinkPaymentType : '',
  IfOther: (formValues.isDebtorTypeIndividual && formValues.receiveCenterlinkPayments === 'true' && formValues.instalmentDeducted === 'true' && formValues.centrelinkPaymentType === 'Other') ? formValues.specifyPaymentType : '',
  // eslint-disable-next-line no-nested-ternary
  CentrelinkYesOrNo: formValues.isDebtorTypeIndividual ? formValues.receiveCenterlinkPayments === 'true' ? formValues.instalmentDeducted === 'true' ? 'Yes' : 'No' : '' : '',
  HowMuchCanYouPayFortnightly: (formValues.isDebtorTypeIndividual && formValues.receiveCenterlinkPayments === 'true' && formValues.instalmentDeducted === 'true') ? formValues.fortnightlyPaymentAmount : '',

  FortnightlyCheckbox: formValues.instalmentFrequency === 'fortnightly',
  CheckBoxSeven: formValues.instalmentFrequency === 'fortnightly',
  MonthlyCheckbox: formValues.instalmentFrequency === 'monthly',
  FortnightlyAmount: formValues.instalmentFrequency === 'fortnightly' ? formValues.instalmentAmount : '',
  MonthlyAmount: formValues.instalmentFrequency === 'monthly' ? formValues.instalmentAmount : '',
  // Individual first instalment date
  InstalmentDD: (formValues.receiveCenterlinkPayments !== 'true' || (formValues.receiveCenterlinkPayments === 'true' && formValues.instalmentDeducted !== 'true')) && formValues.firstInstalmentDate ? (`0${formValues.firstInstalmentDate.getDate()}`).slice(-2) : '',
  InstalmentMM: (formValues.receiveCenterlinkPayments !== 'true' || (formValues.receiveCenterlinkPayments === 'true' && formValues.instalmentDeducted !== 'true')) && formValues.firstInstalmentDate ? (`0${formValues.firstInstalmentDate.getMonth() + 1}`).slice(-2) : '',
  InstalmentYYYY: (formValues.receiveCenterlinkPayments !== 'true' || (formValues.receiveCenterlinkPayments === 'true' && formValues.instalmentDeducted !== 'true')) && formValues.firstInstalmentDate ? formValues.firstInstalmentDate.getFullYear().toString() : '',

  // Comapny first instalment date
  DdTwo: formValues.firstInstalmentDate ? (`0${formValues.firstInstalmentDate.getDate()}`).slice(-2) : '',
  MmTwo: formValues.firstInstalmentDate ? (`0${formValues.firstInstalmentDate.getMonth() + 1}`).slice(-2) : '',
  YyyyTwo: formValues.firstInstalmentDate ? formValues.firstInstalmentDate.getFullYear().toString() : '',

  // Individual Income and Expenses
  YourFortnightlyIncome: formValues.fortnightlyIncomeAfterTax,
  NumberOfDependants: formValues.numberOfDependents,
  YourFortnightlyExpenses: formValues.fortnightlyExpenses,

  // Company Profit and Loss
  ProfitOrLoss: `${formValues.quarterlyProfit} (${formValues.quarterlyLoss})`,

  DoYouHaveAnyExceptionalFinancialCircumstances: formValues.haveExceptionalCircumstances === 'true' ? 'Yes' : 'No',
  ProvideDetailsForAnyCircumstancesYouWouldLikeToBeConsideredForInThisApplication: formValues.haveExceptionalCircumstances === 'true' ? formValues.DetailsOfFinancialCircumstances : '',
  ExpensesOfAttachments: formValues.haveExceptionalCircumstances === 'true' ? totalNumberOfAttachmentsFinancial.toString() : '',
  FinancialAttachments: remoteAttachmentNames,

  StatementThatAppliesToYou: formValues.MoreInfoAboutMatters.join(';'),
  IfYouCannotAffordTheUpfrontPayment: formValues.DetailsForUpfrontPaymentCircumstances,
  MoreInformationOfAttachments: !formValues.NoInfoApplies ? totalNumberOfAttachmentsUpfrontPayment.toString() : '',
  UpfrontPaymentAttachments: remoteAttachmentNames1,

  YouHaveChosenToNotIncludeTheFollowingFinesInThisApplication: checkFines !== '' ? `${checkFines}` : 'No obligations to be excluded.',

  ApplicantsSignature: formValues.declaration ? 'I agree to the statements' : '',
  // Individual
  DeclarationDD: DATE,
  DeclarationMM: MONTH,
  DeclarationYYYY: YEAR.toString(),
  // Company
  DDThree: DATE,
  MMThree: MONTH,
  YYYYThree: YEAR.toString(),

  FooterSubmissionNo1: '1234',
  FooterFormType1: FORM_TYPE,
  FooterObligationNo1: formValues.OBLIGATION_NUMBER,
  FooterSubmittedTime1: SUBMITTED_TIME,

  FooterSubmissionNo2: '1234',
  FooterFormType2: FORM_TYPE,
  FooterObligationNo2: formValues.OBLIGATION_NUMBER,
  FooterSubmittedTime2: SUBMITTED_TIME,

  FooterSubmissionNo3: '1234',
  FooterFormType3: FORM_TYPE,
  FooterObligationNo3: formValues.OBLIGATION_NUMBER,
  FooterSubmittedTime3: SUBMITTED_TIME,
  };
  const excludeKeysFromForm: string[] = [
    'Attachments',
    'InfringementNumber',
    'isDebtorTypeCompany',
    'isDebtorTypeIndividual',
    'isNominatable',
    'HEARING_DATE',
    'DRIVER_LICENCE',
    'VEHICLE_REGISTRATION',
    'SaveYesNo',
    'SaveOptions',
    'saveEmail',
    'saveMobile',
    'IsSubscribedEM',
    'IsSubscribedSMS',
    'EMRemeinderSent',
    'SMSRemeinderSent',
    'SelectedObligationsStep5',
    'FileSizeLimitFinancialDocs',
    'isStep7Continue',
    'FileSizeLimitUpfrontPayment',
    'SelectedObNums',
    'DebtorID',
    'OFFENCE_DATE',
    'DOBRequired',
    'RegistrationRequired',
    'LicenceRequired',
    'HearingDateRequired',
    'OffenceDateRequired',
    'declaration',
    'CompanyPostalAddress',
    'CompanyHasDifferentPostalAddress',
    'CompanyStreetAddress',
    'AcnNo',
    'AbnNo',
    'AbnOrAcnType',
    'CompanyReceiveDocs',
    'CompanyEmail',
    'CompanyHasNoEmail',
    'CompanyContactNumber',
    'CompanyFamilyName',
    'CompanyGivenName',
    'IndividualReceiveDocs',
    'IndividualEmail',
    'IndividualHasNoEmail',
    'IndividualContactNumber',
    'IndividualResidentialAddress',
    'IndividualHasDifferentResidentialAddress',
    'IndividualPostalAddress',
    'StatecountryOfIssue',
    'NoDriverLicence',
    'LicenceState',
    'IndividualGivenName',
    'IndividualFamilyName',
    'quarterlyLoss',
    'quarterlyProfit',
    'fortnightlyExpenses',
    'fortnightlyIncomeAfterTax',
    'numberOfDependents',
    'SelectedObligationsStep5',
    'TotalPaymentArrangementAmount',
    'TotalPaymentArrangementAmountDisplay',
    'AuthorisedObligation',
    'IneligibleObligations',
    'EligibleObligations',
    'ArrangedObligations',
    'AdditionalObligationsNum',
    'MoreInfoAboutMatters',
    'NoInfoApplies',
    'SupportingDocumentsUpfrontPayment',
    'DetailsForUpfrontPaymentCircumstances',
    'SupportingDocUpfrontPaymentNameField',
    'SupportingDocUpfrontPaymentFilesDescription',
    'SupportingDocUpfrontPaymentFileUploadErrorHandle',
    'SupportingDocUpfrontPaymentUploadFileSectionError',
    'SupportingDocUpfrontPaymentFilesProvider',
    'SupportingDocUpfrontPaymentFieldErrorHandle',
    'isStep7ContinueApplicationClicked',
    'receiveCenterlinkPayments',
    'centrelinkPaymentType',
    'instalmentDeducted',
    'specifyPaymentType',
    'centrelinkRefNo',
    'fortnightlyPaymentAmount',
    'centrelinkCheck',
    'instalmentFrequency',
    'instalmentAmount',
    'instalmentNumberOfPayments',
    'firstInstalmentDate',
    'haveExceptionalCircumstances',
    'SupportingFinancialDocuments',
    'DetailsOfFinancialCircumstances',
    'SupportingFinancialDocNameField',
    'SupportingFinancialDocFilesDescription',
    'SupportingFinancialDocFileUploadErrorHandle',
    'SupportingFinancialDocUploadFileSectionError',
    'SupportingFinancialDocFilesProvider',
    'SupportingFinancialDocFieldErrorHandle',
    'DOB',
    'submissionNo',
    'FormExperience',
    'submissionError',
    'ID',
    'PartialFormID',
    'Obligations',
    'FormData',
    'instalmentDeducted',
    'specifyPaymentType',
    'centrelinkRefNo',
    'fortnightlyPaymentAmount',
    'instalmentFrequency',
    'instalmentAmount',
    'instalmentNumberOfPayments',
    'firstInstalmentDate',
    'haveExceptionalCircumstances',
    'fortnightlyExpenses',
    'quarterlyProfit',
    'quarterlyLoss',
    'IndividualFamilyName',
    'IndividualGivenName',
    'LicenceState',
    'NoDriverLicence',
    'StatecountryOfIssue',
    'IndividualPostalAddress',
    'IndividualContactNumberWarning',
    'IndividualEmailWarning',
    'CompanyContactNumberWarning',
    'CompanyEmailWarning',
  ];
  excludeKeysFromForm.forEach((value: string) => {
    delete submitObject[value];
  });
  return submitObject;
};
